import { v4 as uuidv4 } from "uuid";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { ACTIONS, writeLog } from "./auditService";
import { db, storage } from "./firebase";

export function uploadPdf(patientId, file, actor, onProgress) {
  const docId = uuidv4();
  const storagePath = `patients/${patientId}/${docId}/${file.name}`;
  const fileRef = ref(storage, storagePath);

  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(fileRef, file, { contentType: file.type });
    task.on(
      "state_changed",
      (snapshot) => {
        if (onProgress) {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          onProgress(progress);
        }
      },
      reject,
      async () => {
        await setDoc(doc(db, "patients", patientId, "documents", docId), {
          docId,
          fileName: file.name,
          storagePath,
          fileSize: file.size,
          mimeType: file.type,
          uploadedBy: actor.uid,
          uploaderName: actor.displayName,
          uploadedAt: serverTimestamp(),
        });
        await writeLog(actor, ACTIONS.UPLOAD_PDF, "document", docId, `Document: ${file.name}`, { patientId, fileName: file.name });
        resolve({ docId, fileName: file.name, storagePath });
      }
    );
  });
}

export async function uploadPdfs(patientId, files, actor, onItemProgress) {
  const results = [];
  for (const file of files) {
    const result = await uploadPdf(patientId, file, actor, (progress) => {
      if (onItemProgress) onItemProgress(file.name, progress);
    });
    results.push(result);
  }
  return results;
}

export async function listPatientDocuments(patientId) {
  const snap = await getDocs(query(collection(db, "patients", patientId, "documents")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getDocumentCountAcrossPatients(patientIds = []) {
  if (!patientIds.length) return 0;
  const counts = await Promise.all(patientIds.map((id) => getDocs(collection(db, "patients", id, "documents"))));
  return counts.reduce((sum, snap) => sum + snap.size, 0);
}

export async function getMyUploadsCount(uid, patientIds = []) {
  if (!patientIds.length) return 0;
  const counts = await Promise.all(
    patientIds.map((id) =>
      getDocs(query(collection(db, "patients", id, "documents"), where("uploadedBy", "==", uid))).then((snap) => snap.size)
    )
  );
  return counts.reduce((sum, count) => sum + count, 0);
}

export async function getDocumentUrl(storagePath, actor, documentId, action = ACTIONS.VIEW_PDF, fileName = "") {
  if (!storagePath || typeof storagePath !== "string") {
    const error = new Error("Document path is missing");
    error.code = "document/missing-storage-path";
    throw error;
  }

  const targetRef = ref(storage, storagePath);
  const url = await getDownloadURL(targetRef);
  // Audit logging should not block file preview/download.
  writeLog(actor, action, "document", documentId, `Document: ${fileName || storagePath}`).catch(() => {});
  return url;
}

export async function deleteDocument(patientId, record, actor) {
  await deleteObject(ref(storage, record.storagePath));
  const targetId = record.docId || record.id;
  await deleteDoc(doc(db, "patients", patientId, "documents", targetId));
  await writeLog(actor, ACTIONS.DELETE_PDF, "document", targetId, `Document: ${record.fileName}`, { patientId });
}
